import { ApiError, api } from '@/lib/api';
import { formatDateOnly } from '@/lib/format';
import { isRealDate } from '@/components/catalog/form-utils';
import { Badge } from '@/components/ui';
import { denialReason } from '@/lib/permissions';
import { useAuth } from '@/lib/use-auth';
import { InlineField, type InlineSaveResult } from '@/components/ui/InlineField';
import type { ComboboxOption } from '@/components/ui';
import type {
  ClientDeliverable,
  ClientDeliverablePiecePatch,
  ClientDeliverableTypedFields,
  ClientDeliverableUpsertInput,
  EngagementRole,
  LaeSpecialist,
} from '@/lib/types';

/**
 * THE TRACKER'S TYPED FIELDS, EDITED WHERE THEY ARE READ.
 *
 * `/client-deliverables` is a query over quote and invoice LINES with a thin
 * overlay of the fields a person types. This module is the write half of that
 * overlay and nothing else: it can express the keys of
 * `ClientDeliverableTypedFields` and no others, structurally, because that type
 * is what every payload here is built against. **Count that interface; do not
 * trust a number here** — the set has moved in both directions three times.
 *
 * ⚠️ **EVERYTHING DERIVED STAYS READ-ONLY AND NO CONTROL IS OFFERED FOR IT** —
 * company, product, intake, project, regions, the document references and
 * billing status. They are read through the line at request time, so a control
 * over one would either do nothing or write a stored copy that drifts against
 * the invoice the customer was sent, with a 200 and nothing failing.
 *
 * 🔴 **`promised` WAS IN THAT LIST AND LEFT IT — migration 140.** *SUPERSEDED:
 * "company, product, intake, project, regions, **promised**, the document
 * references…"* It is now typed, through `PromisedField`, and the reversal is
 * one of reasoning rather than of taste: the objection was that a stored value
 * would be *a second answer nothing read back*, and the server now reads
 * `COALESCE(<the override>, <the line quantity>)`, so it IS read back and IS
 * what the page renders. **The other eight entries keep the rule** — nothing
 * reads a typed copy of any of them, so a control over one would still be a 200
 * that changes nothing. ⚠️ It exists because 294 live lines carry a quantity
 * contradicting their own description and the money columns are GENERATED from
 * it, so correcting the LINE would re-price about 200 issued invoices.
 *
 * 🔴 **`travel` HAS LEFT THAT LIST TOO — owner's decision, 2026-09-09, and it is
 * an OVERRIDE rather than a replacement.** *SUPERSEDED: "`travel` is the
 * sharpest of the ones that stayed: it is absent from
 * `ClientDeliverableTypedFields` deliberately (migration 115 made it derived),
 * so no payload this module can build expresses it, and the server's `.strict()`
 * schema refuses the key by name if one ever does. Keep it that way — the
 * correction path for a wrong Travel cell is to fix the quote."*
 *
 * **The structural guard that paragraph describes is gone on purpose**, and what
 * replaces it is a distinction rather than an absence: the derivation still runs
 * for every row that has no override, and `travel: null` is how a row is handed
 * back to it. So "fix the quote" is still the right advice for a wrong DERIVED
 * cell — the override is for the answer no quote can carry.
 *
 * ⚠️ **Which means the reader must be able to see WHICH of the two a cell is**,
 * or the override is invisible and the derivation looks unreliable. `TravelField`
 * renders the effective answer and the state it came from, and it seeds itself
 * from `row.travelOverride` — never from `row.travel`, which has already folded
 * the two together and cannot be unfolded.
 *
 * ⚠️ **`pending` is derived, signed, and moves when EITHER count is saved.**
 * Overdelivery is real, so it is never clamped and never written. It comes back
 * recomputed on the response, which is one more reason the row is REPLACED by
 * what the server sent rather than patched locally — and it matters more now
 * that `promised` is writable: correcting a promise DOWN past an existing
 * completed count produces a negative `pending` on the row somebody has just
 * fixed, which a local patch would have to know to recompute.
 *
 * ## Why this consumes `InlineField` rather than growing a page-local editor
 *
 * `InlineField` already owns the three rules this screen needs and would
 * otherwise have to re-derive: an untouched field fires no request (an empty
 * PATCH here is a 422), a failed save keeps what was typed, and a save always
 * says so. It is also where per-field state lives — phase, draft and notice are
 * that instance's — so "one cell saving must not disable or report on another"
 * is structural rather than something this file has to arrange. A second
 * implementation would agree with it the day it was written and drift on the
 * first correction.
 *
 * It is consumed at its published API and this module reports only `saved`,
 * `warning` and `error`; `InlineSaveResult` is a union that other callers may
 * grow, and reporting a member is opt-in.
 */

/* -------------------------------------------------------------------------
 * The verb
 * --------------------------------------------------------------------- */

/**
 * Writes ONE key and returns the row the server sent back.
 *
 * ⚠️ **`row.id` DECIDES THE VERB, and `row.key` is the identity.** The tracker
 * is a query over lines, so a line nobody has typed against has no overlay row
 * and arrives with `id: null` — there is nothing for a `PATCH` to name. That row
 * takes the `PUT` upsert, which names the LINE instead; a row that already has
 * an id takes the `PATCH`. Verified against `client-deliverables.routes.ts`
 * (`PUT /` at the collection, `PATCH /:id`) and `attachedWriteSchema`, which
 * refines exactly one of the two line ids.
 *
 * ⚠️ **After a successful upsert the row GAINS an id, so the NEXT edit on it
 * must `PATCH`.** That is why the caller replaces its row with the returned
 * record and never patches its local copy from the value it sent: the response
 * is read back through the same union the list uses, so it carries the new id,
 * the recomputed `pending`, and every derived field the write never saw. A
 * locally-patched copy would keep `id: null`, and every later edit would upsert
 * against the line again — which happens to work, right up until the row is a
 * standalone one, where there is no line to name at all.
 *
 * `key` is line-derived for an attached row (`q:` / `i:`) and id-derived only
 * for a standalone one, so it is stable across the transition and is what the
 * caller matches on.
 */
async function writeTypedField(
  row: ClientDeliverable,
  patch: ClientDeliverableTypedFields,
): Promise<ClientDeliverable> {
  const piecePatch = row.pieceId === null ? null : piecePatchOf(patch);
  if (row.pieceId !== null && piecePatch !== null) {
    const response = await api.updateClientDeliverablePiece(row.pieceId, piecePatch);
    return response.data;
  }
  if (row.id !== null) {
    const response = await api.updateClientDeliverable(row.id, patch);
    return response.data;
  }
  if (row.line === null) {
    // Unreachable on the contract — a row with no id and no line belongs to
    // neither population — but `throw` rather than assert, so a shape that
    // arrives anyway fails loudly instead of silently writing nothing.
    throw new Error('This row names no line, so there is nothing to record progress against.');
  }
  const body: ClientDeliverableUpsertInput =
    row.line.kind === 'quote'
      ? { quoteProductId: row.line.id, ...patch }
      : { invoiceProductId: row.line.id, ...patch };
  const response = await api.upsertClientDeliverable(body);
  return response.data;
}

/**
 * The part of a typed-field patch that belongs to a PIECE, or `null` when it
 * names none of the per-piece keys.
 *
 * ⚠️ **On a split row the fields live in two places.** Completed, start date and
 * end date are the PIECE's own and go to `PATCH /client-deliverables/pieces/:id`;
 * everything else (POC, specialist, booking spec, engagement, billing note, ads
 * plan, travel) is SHARED across the pieces and stays on the row's overlay, so
 * editing it on one piece edits every piece. Every save here sends one key, so a
 * patch is either wholly a piece patch or wholly an overlay patch — it is never
 * split across the two requests.
 *
 * ⚠️ **`promised` is NOT a piece key on this endpoint** — a piece's quantity
 * moves through the split dialog's whole-set PUT, because the pieces must keep
 * adding up to the row. `PromisedField` is disabled on a piece row for that
 * reason, so the key never reaches here from one.
 */
function piecePatchOf(patch: ClientDeliverableTypedFields): ClientDeliverablePiecePatch | null {
  const out: ClientDeliverablePiecePatch = {};
  let named = false;
  if (patch.completed !== undefined) {
    out.completed = patch.completed;
    named = true;
  }
  if (patch.startDate !== undefined) {
    out.startDate = patch.startDate;
    named = true;
  }
  if (patch.endDate !== undefined) {
    out.endDate = patch.endDate;
    named = true;
  }
  return named ? out : null;
}

/* -------------------------------------------------------------------------
 * Clear semantics, measured from the server's own schema
 * --------------------------------------------------------------------- */

/**
 * `bookingSpec`, `billingNote` and `adsPlan` are `optionalText(n)`, which is
 * `.nullish()` with a `'' -> null` transform: **both `''` and `null` clear.**
 * We send `null`, so this codebase never learns the looser habit — an empty
 * string is a value everywhere else in this app.
 *
 * The value is otherwise sent as typed. The server trims, and trimming here as
 * well would make the draft and the stored value disagree by a space, which
 * `InlineField`'s unchanged-value test would then read as a change.
 */
function textOrNull(next: string): string | null {
  return next.trim() === '' ? null : next;
}

/** `optionalDate()` — `''` and `null` both clear. We send `null`. */
function dateOrNull(next: string): string | null {
  return next === '' ? null : next;
}

/**
 * The maximum lengths are the server's own, so the control stops where the API
 * would rather than collecting a refusal. `bookingSpec` is 4,000 because it
 * holds a whole booking specification; the other two are 2,000.
 */
const BOOKING_SPEC_MAX = 4_000;
const BILLING_NOTE_MAX = 2_000;
const ADS_PLAN_MAX = 2_000;
/** The source line's internal note, `optionalText(2_000)` server-side. */
const LINE_NOTE_MAX = 2_000;

/** `count()` is `INT UNSIGNED`: 0 .. 4,294,967,295. Shared by both counts. */
const COMPLETED_MAX = 4_294_967_295;

/**
 * ⚠️ **`promised` IS THE ONE KEY WHOSE EMPTY STATE DEPENDS ON THE POPULATION**,
 * and the population is `row.line`.
 *
 *   attached (`row.line !== null`)   clearing is the FEATURE — it drops the
 *                                    override and hands the figure back to the
 *                                    line quantity.
 *   standalone (`row.line === null`) clearing is a 422 naming `promised`. There
 *                                    is no line to fall back to, so the column
 *                                    is required (branch 3 of the population
 *                                    CHECK).
 *
 * So the refusal is made HERE on a standalone row, in the same shape as
 * `completed` one field over, rather than collected from the server. Letting it
 * through would put a 422 in front of somebody looking at a box they can see is
 * empty, which reads as the app being broken rather than as the column having no
 * empty state on this population.
 *
 * ⚠️ `0` IS NEVER REFUSED, on either population. It is a real claim — cancelled
 * work, nothing promised — and it is one keystroke from the empty box this
 * refuses, so a validator written on falsiness would take both.
 */
function validatePromised(next: string, attached: boolean): string | null {
  const trimmed = next.trim();
  if (trimmed === '') {
    return attached
      ? null
      : 'Enter a number. This deliverable has no quote or invoice line to fall back on, so use 0 for nothing promised.';
  }
  if (!/^\d+$/.test(trimmed)) return 'Enter a whole number of units promised.';
  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed) || parsed > COMPLETED_MAX) {
    return `Enter a whole number no greater than ${COMPLETED_MAX.toLocaleString()}.`;
  }
  return null;
}

/**
 * ⚠️ **`completed` IS THE ONE TYPED FIELD THAT CAN NEVER BE CLEARED**, and the
 * asymmetry is easy to miss because most of its neighbours always can.
 *
 * ⚠️ *SUPERSEDED: "its seven neighbours all can."* `promised` joined the set in
 * migration 140 and clears on an ATTACHED row only — see `validatePromised`. So
 * there are now two shapes of exception and they are not the same shape: this
 * one is refused everywhere, that one is refused on one population. **Do not
 * fold them into one rule**; a validator that refused an empty `promised`
 * outright would take away the only way to un-do an override.
 *
 * Measured in `client-deliverables.schema.ts`: it is `count().optional()`, and
 * `count()` is `union([number, non-empty string]) -> Number -> int().min(0)`.
 * `.optional()` and not `.nullish()`, so **`null` is a 422 and `''` is a 422** —
 * omitting the key is the only way not to write it. There is no "not recorded"
 * state for a count; zero is the figure for nothing delivered yet.
 *
 * So an emptied box is refused HERE, with a sentence saying which value means
 * "none". Letting it through would collect a 422 naming a field the reader can
 * see is empty, which reads as the app being broken rather than as the column
 * having no empty state.
 */
function validateCompleted(next: string): string | null {
  const trimmed = next.trim();
  if (trimmed === '') return 'Enter a number. Use 0 for nothing delivered yet — this cannot be left blank.';
  if (!/^\d+$/.test(trimmed)) return 'Enter a whole number of units delivered.';
  const parsed = Number(trimmed);
  if (!Number.isSafeInteger(parsed) || parsed > COMPLETED_MAX) {
    return `Enter a whole number no greater than ${COMPLETED_MAX.toLocaleString()}.`;
  }
  return null;
}

/**
 * A part-typed date reads back from a native date control as `''`, which is
 * indistinguishable from a cleared field. Here clearing IS legal, so the only
 * thing to refuse is a complete value that is not a real calendar date —
 * `2026-02-30` sails through a regex and is refused by MySQL as a driver error.
 */
function validateDate(next: string): string | null {
  if (next === '') return null;
  return isRealDate(next) ? null : 'Enter a real date.';
}

const ENGAGEMENT_ROLE_OPTIONS: readonly ComboboxOption[] = [
  { value: 'full_time', label: 'Full-time' },
  { value: 'part_time', label: 'Part-time' },
];

/* -------------------------------------------------------------------------
 * Travel: three states, on two keys
 * --------------------------------------------------------------------- */

/**
 * The words a travel answer is rendered in, in ONE place.
 *
 * The badge, the picker's two options and the column funnel on
 * `ClientDeliverablesPage` all read this, so a row cannot say "Travelling" in
 * one and something else in the other — which is what would let somebody filter
 * to "not travelling" and be handed rows whose own cell disagrees.
 */
export function travelLabel(travel: boolean): string {
  return travel ? 'Travelling' : 'No travel';
}

/**
 * The empty option, and it says what CHOOSING it does rather than being a bare
 * dash — the same shape as the POC picker's "Use the client success manager".
 * It is an option label, not helper text: it is the only thing standing between
 * a reader and the belief that clearing this control means "no travel".
 *
 * ⚠️ **It is deliberately NOT branched for a standalone row**, which has no
 * document at all and therefore derives `false` every time. Saying so here would
 * be an explanation of a data state printed in advance — the sentence the
 * helper-text rule forbids. The DISPLAY says it instead, by showing the answer
 * the choice produced and marking it `Nothing recorded`: the consequence is
 * shown rather than described.
 */
const TRAVEL_DERIVE_LABEL = 'Use the travel lines on the document';

const TRAVEL_YES = 'yes';
const TRAVEL_NO = 'no';

const TRAVEL_OPTIONS: readonly ComboboxOption[] = [
  { value: TRAVEL_YES, label: travelLabel(true) },
  { value: TRAVEL_NO, label: travelLabel(false) },
];

/**
 * The control's value, seeded from the OVERRIDE and never from the effective
 * answer.
 *
 * ⚠️ `row.travel` has already folded the override into the derivation and cannot
 * be unfolded: a row deriving `true` and a row overridden to `true` are the same
 * boolean there. Seeding from it would silently promote every derived row to an
 * overridden one the first time anybody opened the picker and picked what was
 * already showing — a write, on a row nobody meant to pin.
 */
function travelValueOf(override: boolean | null): string {
  if (override === null) return '';
  return override ? TRAVEL_YES : TRAVEL_NO;
}

/**
 * The value going out. **`null` is DERIVE, and it is not the same instruction as
 * `false`** — one drops the override, the other records that this work carries
 * no travel.
 *
 * The default arm is the empty option, which is the only other string `Combobox`
 * can emit here. It resolves to `null` rather than to a boolean deliberately: of
 * the three outcomes that is the one that asserts nothing and is undone by
 * picking again, so an unexpected value costs a dropped override rather than a
 * confident wrong answer on a customer's delivery record.
 */
function travelWriteOf(next: string): boolean | null {
  switch (next) {
    case TRAVEL_YES:
      return true;
    case TRAVEL_NO:
      return false;
    default:
      return null;
  }
}

/**
 * ⚠️ The label a READER sees, never the slug on the wire.
 *
 * `InlineField` falls back to the raw `value` when no `display` is passed, and
 * that is `full_time` — a wire token rendered to a customer-facing screen. Both
 * this and the dates below pass a `display` for that reason: the slug and the
 * ISO date are correct values and are the wrong words.
 */
const ENGAGEMENT_ROLE_LABELS: Record<EngagementRole, string> = {
  full_time: 'Full-time',
  part_time: 'Part-time',
};

/* -------------------------------------------------------------------------
 * The writer
 * --------------------------------------------------------------------- */

export interface TrackerRowWriter {
  /** Whether the reader may write these columns at all. */
  canEdit: boolean;
  /** Why not, in one sentence, when they may not. */
  disabledReason: string | undefined;
  /** Performs one write and reports in `InlineField`'s vocabulary. */
  save: (
    requestKey: keyof ClientDeliverableTypedFields,
    patch: ClientDeliverableTypedFields,
  ) => Promise<InlineSaveResult>;
}

/**
 * Gating and the write, once per mounted row.
 *
 * ⚠️ **Gated on `ability.canUpdate('deliverables')`, never on the role and never
 * on `/permissions/me`.** `/client-deliverables` carries the ordinary CRM guard
 * profile — reads open to every role including `viewer`, writes `WRITE_ROLES`,
 * delete admin — and it is deliberately NOT one of the permission-set modules.
 *
 * The controls are mounted UNCONDITIONALLY and self-gate, so a reader without
 * the permission still sees every value with `denialReason()` under it rather
 * than an absence. Hiding them would make a read-only account look like a
 * tracker with no data in those columns.
 */
export function useTrackerRowWriter(
  row: ClientDeliverable,
  onSaved: (next: ClientDeliverable) => void,
  /**
   * Fired after a successful write on a SPLIT row (`row.pieceId !== null`), so
   * the page can refetch. Optional and additive: the pages that mount this hook
   * without it behave exactly as before.
   *
   * ⚠️ `onSaved` alone is not enough there. The pieces of one row SHARE the
   * overlay fields, so a POC or specialist written through one piece changes
   * every sibling on screen, and replacing just the row that was edited leaves
   * the others stating the old value with nothing failing.
   */
  onSplitRowWritten?: () => void,
): TrackerRowWriter {
  const { ability } = useAuth();
  const canUpdate = ability.canUpdate('deliverables');
  const permissionReason = denialReason(ability, 'update', 'deliverables');
  /*
   * A row that names neither an overlay row nor a line cannot be written to by
   * anybody, and that is a different sentence from a permission refusal — one
   * sends the reader to an administrator for access that would change nothing.
   *
   * 🔴 **THE INCLUSION CLAUSE IS NOT REDUNDANT AND MUST NOT BE TIDIED AWAY —
   * added 2026-09-12.** An inclusion row (`quote_inclusion` / `invoice_inclusion`)
   * derives from a campaign inclusion sitting ON a line, and has no overlay row
   * of its own: `d_client_deliverables` overlays a LINE, and an inclusion inside
   * a line is not one. So `row.id` is `null` PERMANENTLY there — but `row.line`
   * is a REAL line, which is exactly what makes this dangerous rather than
   * merely wrong.
   *
   * Without this clause the two tests below disagree in the worst possible way:
   * the gate `row.id !== null || row.line !== null` passes on the line, and the
   * VERB test `row.id === null` then selects the `PUT` upsert naming
   * `quoteProductId` / `invoiceProductId`. That line exists, so the write
   * **succeeds** — and the figure somebody typed against the inclusion lands on
   * the PARENT LINE's deliverable, with a `200`, no error, and both rows on the
   * same screen. The wrong one visibly changes.
   *
   * ⚠️ **THIS IS THE SHARED GUARD, AND IT IS THE ONLY ONE ON TWO OF THE THREE
   * PAGES.** `ClientDeliverablesPage` also suppresses these editors at the cell,
   * which is the better place to refuse — but `AccountsDashboardPage` and
   * `FutureBillablesTrackerPage` mount this hook too and have no such branch.
   * Defence in depth is the point: a page added tomorrow gets the refusal for
   * free instead of inheriting the defect.
   */
  const isInclusionRow = row.source === 'quote_inclusion' || row.source === 'invoice_inclusion';
  const writable = !isInclusionRow && (row.id !== null || row.line !== null);

  const canEdit = canUpdate && writable;
  /*
   * ⚠️ **THE TWO UNWRITABLE CASES NEED TWO SENTENCES, because one of them would
   * otherwise be a false statement.** "This row names no line" was true of the
   * only unwritable population there used to be; an inclusion row names a line
   * perfectly well, and the reader can SEE its quote or invoice number in the
   * same row — so telling them there is no line reads as the app being wrong
   * about a document in front of them, and sends them looking for a fault.
   *
   * The inclusion sentence says what is true — progress is recorded against the
   * line, which is the row directly above — so the reader knows where to go
   * rather than only that they cannot type here. Neither is helper text: both
   * name a state the reader cannot otherwise see, which is the documented
   * exception.
   */
  const disabledReason = !canUpdate
    ? (permissionReason ?? 'Your account does not have permission to change this.')
    : isInclusionRow
      ? 'Progress is recorded against the line item this inclusion sits on, not against the inclusion.'
      : !writable
        ? 'This row names no line, so there is nothing to record progress against.'
        : undefined;

  /*
   * A plain function, not a `useCallback`: it is only ever called from an
   * `InlineField`'s commit handler, which is a fresh closure every render
   * anyway, so memoising it would assert a stability nothing depends on and
   * would need `row` in a dependency list where a stale one writes the wrong
   * verb.
   */
  const save = async (
    requestKey: keyof ClientDeliverableTypedFields,
    patch: ClientDeliverableTypedFields,
  ): Promise<InlineSaveResult> => {
    try {
      onSaved(await writeTypedField(row, patch));
      if (row.pieceId !== null || row.piece !== null) onSplitRowWritten?.();
      return { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        /*
         * The specialist list lives in another system (the SEED admin backend),
         * so a specialist write can fail on THAT system rather than on this one:
         * unreachable (`UPSTREAM_UNAVAILABLE`), or EMERGE's key not allowed to
         * read the list (`LAE_ACCESS_DENIED`). Neither is about the value typed,
         * so neither marks the draft invalid — the server's own sentence says
         * which, and that is the actionable half.
         */
        if (
          caught.status === 502 &&
          (caught.code === 'UPSTREAM_UNAVAILABLE' || caught.code === 'LAE_ACCESS_DENIED')
        ) {
          return { status: 'warning', message: `${caught.message} Nothing was saved.` };
        }
        /*
         * ⚠️ A 403 carries no `details` at all, so it can never be a field
         * error — it is rendered as prose beside the control instead. It can
         * arrive even though `canUpdate` said otherwise, because a role can
         * change under a live access token, which lasts over five days. The
         * gate above is the optimisation; this branch is the guarantee.
         */
        if (caught.status === 403) {
          return {
            status: 'error',
            message: `${
              denialReason(ability, 'update', 'deliverables') ??
              'Your account no longer has permission to change this row.'
            } Nothing was saved.`,
          };
        }
        /*
         * On the `PATCH` this is the overlay row; on the `PUT` it is the line
         * named in the body. Either way the thing being written to has gone,
         * and re-typing cannot bring it back.
         */
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This row is no longer on the tracker, so the change could not be saved.',
          };
        }
        /*
         * Split against the ONE key this save sent. A 422 whose path names it
         * belongs on this control; everything else — a `.strict()` rejection
         * (`path: ''` plus a `key`), the "at least one field" refusal, or a
         * path naming a key with no control here — is unroutable.
         *
         * There is one control and one message slot per field here, so an
         * unroutable refusal still renders exactly where it belongs and needs
         * no section-level notice. It is reported as `warning` and not `error`:
         * the request is over and the value did not take, so marking the draft
         * invalid would state something false about it, and holding the editor
         * open would invite a retype refused identically.
         */
        const { fields, unroutable } = caught.routeFieldErrors([requestKey]);
        const fieldText = Object.values(fields).join(' ');
        if (fieldText !== '') return { status: 'error', message: fieldText };
        if (unroutable.length > 0) {
          return { status: 'warning', message: `${unroutable.join(' ')} Nothing was saved.` };
        }
        return { status: 'error', message: caught.message };
      }
      /*
       * The unattached-row `throw` above lands here too, which is why its
       * message is a sentence a reader can act on rather than an assertion.
       */
      if (caught instanceof Error && caught.message !== '') {
        return { status: 'error', message: caught.message };
      }
      return {
        status: 'error',
        message: 'Could not reach the server. Check your connection and try again.',
      };
    }
  };

  return { canEdit, disabledReason, save };
}

/* -------------------------------------------------------------------------
 * The fields
 * --------------------------------------------------------------------- */

export interface TrackerFieldProps {
  row: ClientDeliverable;
  writer: TrackerRowWriter;
}

/**
 * Everything a field needs from its writer, in one place, so no call site can
 * gate one control differently from its neighbour.
 */
function gate(writer: TrackerRowWriter) {
  return {
    disabled: !writer.canEdit,
    ...(writer.disabledReason === undefined ? {} : { disabledReason: writer.disabledReason }),
  };
}

/**
 * The workbook's POC column — READS two fields and WRITES one.
 *
 * Owner's rule, 2026-09-01: show the client success manager, and the delivery
 * lead instead wherever one is set for those deliverables. Only the delivery
 * lead is typed here; the CSM belongs to the COMPANY and is deliberately not
 * writable from this page, because a second copy stored on the deliverable
 * would drift against the account with nothing failing.
 *
 * ⚠️ **THE CELL CAN SHOW A NAME WHILE THE CONTROL IS EMPTY, and that is the one
 * thing about this field a reader could misread.** An empty picker means "no
 * delivery lead on this row", not "no POC" — the CSM is still displayed. So the
 * empty option says what choosing it DOES rather than being a bare dash, and
 * the display carries a `title` naming which of the two people is on screen.
 * Neither is helper text: both name a state the reader cannot otherwise see.
 *
 * ⚠️ The options are NARROWED to the `delivery_lead` group by the caller, and
 * the server does NOT enforce that — see the field's note in the backend
 * schema. Somebody missing from the picker is fixed by adding them to the
 * group, which is free because nothing else enforces against it.
 */
export function DeliveryLeadField({
  row,
  writer,
  options,
}: TrackerFieldProps & { options: ComboboxOption[] }) {
  const lead = row.deliveryLead;
  const csm = row.clientSuccessManager;
  const shown = lead ?? csm;

  return (
    <InlineField
      label="POC"
      kind="picker"
      value={lead === null ? '' : String(lead.id)}
      options={options}
      emptyOptionLabel="Use the client success manager"
      display={
        shown === null ? undefined : (
          <span
            className="block truncate"
            title={
              lead === null
                ? `${shown.name} — the client success manager on this company. No delivery lead is set on this row.`
                : `${shown.name} — the delivery lead on this row.`
            }
          >
            {shown.name}
          </span>
        )
      }
      onSave={(next) =>
        writer.save('deliveryLeadId', {
          // `''` is the empty option and clears the row's own lead, which hands
          // the cell back to the company's CSM. `null` is what the API takes;
          // an empty string would be a 422 on a user id.
          deliveryLeadId: next === '' ? null : Number(next),
        })
      }
      {...gate(writer)}
    />
  );
}

/**
 * The picker's options: every LAE representative, plus the row's stored one
 * when it is no longer in the list.
 *
 * ⚠️ **The stored value is injected back, never dropped.** The list is read from
 * another system and a representative can leave it; without the injected row
 * the picker would render its placeholder over a real stored specialist, and the
 * next pick would silently reassign it. The NAME comes from the row's own
 * snapshot, which is why the server stores one.
 */
function specialistOptions(
  specialists: readonly LaeSpecialist[],
  stored: ClientDeliverable['specialist'],
): ComboboxOption[] {
  const options: ComboboxOption[] = specialists.map((specialist) => ({
    value: String(specialist.id),
    label: specialist.name,
    ...(specialist.role ? { hint: specialist.role } : {}),
  }));
  if (stored !== null && !specialists.some((specialist) => specialist.id === stored.id)) {
    options.unshift({ value: String(stored.id), label: stored.name, hint: 'No longer listed' });
  }
  return options;
}

/**
 * The LAE specialist — one per row, SHARED across the pieces of a split row.
 *
 * ⚠️ **A failed or unread list DISABLES the control with its reason, and never
 * offers an empty list.** With no options the only reachable outcome is clearing
 * a value the reader can see. `specialists === null` covers both "still loading"
 * and "failed"; `specialistsError` is what tells them apart.
 *
 * A permission refusal outranks the list's state: a reader who may not write
 * this row is told that once, rather than about a list they could not use anyway.
 */
export function SpecialistField({
  row,
  writer,
  specialists,
  specialistsError,
}: TrackerFieldProps & {
  specialists: LaeSpecialist[] | null;
  specialistsError: string | null;
}) {
  const stored = row.specialist;
  const listReason =
    specialists !== null
      ? undefined
      : (specialistsError ?? 'The specialist list has not loaded yet.');
  const reason = !writer.canEdit ? writer.disabledReason : listReason;
  const disabled = !writer.canEdit || specialists === null;

  return (
    <InlineField
      label="Specialist"
      kind="picker"
      value={stored === null ? '' : String(stored.id)}
      options={specialists === null ? [] : specialistOptions(specialists, stored)}
      optionsLoading={specialists === null && specialistsError === null}
      emptyOptionLabel="No specialist"
      searchPlaceholder="Search specialists…"
      display={
        stored === null ? undefined : (
          <span className="block truncate" title={stored.name}>
            {stored.name}
          </span>
        )
      }
      onSave={(next) =>
        writer.save('specialistId', {
          // `''` is the empty option and clears both the id and the name
          // snapshot server-side; an empty string would be a 422 on an id.
          specialistId: next === '' ? null : Number(next),
        })
      }
      disabled={disabled}
      {...(disabled && reason !== undefined ? { disabledReason: reason } : {})}
    />
  );
}

/** The specialist as text, for a row whose editors are suppressed. */
export function ReadOnlySpecialist({ row }: { row: ClientDeliverable }) {
  const stored = row.specialist;
  if (stored === null) return <span className="text-xs text-fg-subtle">—</span>;
  return (
    <span className="block truncate" title={stored.name}>
      {stored.name}
    </span>
  );
}

/**
 * Units PROMISED, which is an override on an attached row.
 *
 * ⚠️ **THE VALUE SHOWN IS ALREADY RESOLVED AND THE CONTROL CANNOT TELL WHICH IT
 * IS.** `row.promised` is `COALESCE(<the override>, <the line quantity>)`
 * server-side and there is no flag on the wire saying which one arrived — so
 * this must not mark a row as corrected, and the placeholder must not promise a
 * figure it cannot name. Editing an untouched row therefore starts from the line
 * quantity, which is the right default: the person is correcting THAT number.
 *
 * ⚠️ **The placeholder is the CONSEQUENCE OF CLEARING, and it is not helper
 * text.** Emptying this box does something invisible — the cell keeps a number,
 * a different one — and no other state on the screen says so. Without it,
 * clearing reads as a save that did not press. On a standalone row it says the
 * opposite, because there clearing is refused, and `validatePromised` refuses it
 * before a request is made.
 *
 * ⚠️ **It writes the SAME KEY on both populations.** The server decides; nothing
 * here branches the payload, only the wording and the local refusal.
 *
 * ✅ **MOUNTED — corrected 2026-10-04.** *SUPERSEDED: "🔴 **NOT MOUNTED YET —
 * as of 2026-09-02 `ClientDeliverablesPage` still renders `{row.promised}` as
 * plain text in both of its layouts** … so this component has ZERO call sites."*
 * `ClientDeliverablesPage` imports and renders it; grep the import rather than
 * trusting either sentence.
 *
 * ⚠️ **On a PIECE row it is disabled.** A piece's quantity is a share of the
 * row's promised and the pieces must add up to it, so it moves only through the
 * split dialog's whole-set write. Writing `promised` here would land on the
 * SHARED overlay — the whole row's figure — from a cell showing one piece's.
 */
export function PromisedField({ row, writer }: TrackerFieldProps) {
  const attached = row.line !== null;
  if (row.pieceId !== null) {
    const reason = writer.canEdit ? "Change a piece's quantity with Split." : writer.disabledReason;
    return (
      <InlineField
        label="Promised"
        value={String(row.promised)}
        display={<span className="tabular">{row.promised}</span>}
        onSave={() => Promise.resolve({ status: 'saved' })}
        disabled
        {...(reason === undefined ? {} : { disabledReason: reason })}
      />
    );
  }
  return (
    <InlineField
      label="Promised"
      value={String(row.promised)}
      display={<span className="tabular">{row.promised}</span>}
      inputMode="numeric"
      /*
       * ⚠️ **NO INSTRUCTION-SHAPED PLACEHOLDER — owner's ruling, 2026-09-05.**
       * It read "Clear to use the line item quantity" / "Required — use 0 for
       * nothing promised", which was advice printed in advance of any refusal.
       * **`validatePromised` still says both things when they actually apply**,
       * and a validation message is a keeper; do not restore the placeholder.
       */
      validate={(next) => validatePromised(next, attached)}
      onSave={(next) => {
        const trimmed = next.trim();
        return writer.save('promised', {
          // `''` reaches here only on an ATTACHED row — `validatePromised`
          // refuses it on a standalone one before this runs — and `null` is what
          // the API takes for "drop the override". An empty string is a 422 on a
          // count, and `0` would be a claim the person did not make.
          promised: trimmed === '' ? null : Number(trimmed),
        });
      }}
      {...gate(writer)}
    />
  );
}

/** Units delivered. The one field with no empty state — see `validateCompleted`. */
export function CompletedField({ row, writer }: TrackerFieldProps) {
  return (
    <InlineField
      label="Completed"
      value={String(row.completed)}
      display={<span className="tabular">{row.completed}</span>}
      inputMode="numeric"
      validate={validateCompleted}
      onSave={(next) => writer.save('completed', { completed: Number(next.trim()) })}
      {...gate(writer)}
    />
  );
}

/*
 * ⚠️ **`AccountOwnerField` STOOD HERE — the workbook's "POC" / "LAE Specialist"
 * — and was removed on 2026-08-31 on the owner's instruction**, in the same
 * pass that retired Owner on companies, contacts, deals, quotes and invoices.
 *
 * `accountOwnerId` left `ClientDeliverableTypedFields` with it, so the typed
 * set is SEVEN keys and `writer.save` cannot name it — the guard is structural,
 * not a control somebody deleted. That matters more here than on the document
 * forms, because this writer has TWO paths: a `PATCH` when `row.id` is set and
 * a `PUT` upsert NAMING THE LINE when it is null. A key left declared could
 * leak through either, and on the `PUT` it would be written into a row that did
 * not previously exist.
 *
 * `row.accountOwner` is still returned by the API and the column is untouched;
 * this app stopped rendering and writing it. Re-adding the control is an owner
 * decision and needs the key back on the interface first — at which point the
 * warning that lived here applies again: **it is SEED STAFF, a `d_users` row,
 * NOT a client contact.** The old entity's `pointOfContact` is a `d_contacts`
 * row, one word apart and a different person, so the picker must never be
 * pointed at a contacts list.
 */

/** Booking specifications — free text, 4,000 characters, `null` clears. */
export function BookingSpecField({ row, writer }: TrackerFieldProps) {
  return (
    <InlineField
      label="Booking specifications"
      kind="multiline"
      value={row.bookingSpec ?? ''}
      maxLength={BOOKING_SPEC_MAX}
      rows={4}
      display={
        row.bookingSpec && row.bookingSpec.trim() !== '' ? (
          <span className="text-xs text-fg-secondary [overflow-wrap:anywhere]">
            {row.bookingSpec}
          </span>
        ) : undefined
      }
      onSave={(next) => writer.save('bookingSpec', { bookingSpec: textOrNull(next) })}
      {...gate(writer)}
    />
  );
}

/**
 * Full-time or part-time.
 *
 * A closed ENUM on the wire rather than a reference master — the set is fixed by
 * the contract the work is sold under, not curated by an administrator — so
 * there is no retired-option trap here and no master to fetch.
 */
export function EngagementRoleField({ row, writer }: TrackerFieldProps) {
  return (
    <InlineField
      label="Engagement"
      kind="picker"
      value={row.engagementRole ?? ''}
      options={[...ENGAGEMENT_ROLE_OPTIONS]}
      emptyOptionLabel="Not recorded"
      display={
        row.engagementRole ? (
          <span>
            <Badge tone="neutral" variant="outline">{ENGAGEMENT_ROLE_LABELS[row.engagementRole]}</Badge>
          </span>
        ) : undefined
      }
      onSave={(next) =>
        writer.save('engagementRole', {
          engagementRole: next === '' ? null : (next as EngagementRole),
        })
      }
      {...gate(writer)}
    />
  );
}

/**
 * Travel — an OVERRIDE over a derivation that keeps running underneath it.
 *
 * Owner's decision, 2026-09-09. Until then this column was derived and carried
 * no control at all: the server answers it from the catalogue, by looking for a
 * line selling the product's `travelProduct` on the same document and project.
 * That answer is right for most rows and unreachable for some — travel agreed
 * outside the lines, and every standalone row, which has no document for a
 * sibling line to sit on and so can only ever derive `false`.
 *
 * ## Three states, and the cell has to show which one it is in
 *
 * ⚠️ **A tick would be wrong here, and that is the whole reason
 * `travelOverride` exists as a second key.** "Travelling because the quote sells
 * a travel line" and "Travelling because a person said so" are different facts
 * with different correction paths — the first is fixed on the document or the
 * product, the second only here — and a control that rendered them identically
 * would send somebody editing a quote to change an answer no quote controls.
 *
 * So the display is the effective answer PLUS the state it came from:
 *
 *   Travelling / No travel   + `Set on this row`     someone overruled it
 *   Travelling / No travel   + `From the document`   deriving, and there is one
 *   No travel                + `Nothing recorded`    deriving, standalone row
 *
 * ⚠️ **The third line is not a nicety.** `From the document` on a standalone row
 * would be a false statement, printed one column away from the `Added manually`
 * marker that says there is no document — a page contradicting itself in a
 * single row. The marker is a value label rather than helper text by this
 * codebase's own test: it names a state the reader cannot otherwise see, and
 * removing it makes two different facts look identical.
 *
 * ⚠️ **`null` and `false` are one keystroke apart and mean opposite things** —
 * `null` hands the row back to the derivation, `false` records that this work
 * carries no travel. `travelWriteOf` is the only place that mapping lives, and
 * the write goes out on the `travel` key while the control is seeded from
 * `travelOverride`. Read key ≠ write key here, deliberately.
 */
export function TravelField({ row, writer }: TrackerFieldProps) {
  const overridden = row.travelOverride !== null;
  const attached = row.line !== null;
  return (
    <InlineField
      label="Travel"
      kind="picker"
      value={travelValueOf(row.travelOverride)}
      options={[...TRAVEL_OPTIONS]}
      emptyOptionLabel={TRAVEL_DERIVE_LABEL}
      /*
       * Spans throughout: this renders inside the trigger `<button>`, whose
       * content model is phrasing content, so a `<div>` here would be invalid
       * markup that browsers silently reflow.
       */
      display={
        <span className="grid gap-0.5">
          <span>
            {/* Tone is a second signal and never the only one — the word carries it. */}
            <Badge tone={row.travel ? 'info' : 'neutral'} dot>
              {travelLabel(row.travel)}
            </Badge>
          </span>
          <span className="text-2xs text-fg-muted">
            {overridden ? 'Set on this row' : attached ? 'From the document' : 'Nothing recorded'}
          </span>
        </span>
      }
      onSave={(next) => writer.save('travel', { travel: travelWriteOf(next) })}
      {...gate(writer)}
    />
  );
}

/**
 * The row's own window.
 *
 * ⚠️ **These are NOT a pair that must travel together**, unlike a project's
 * `{startDate, endDate}` and unlike a quote's `{issueDate, validTill}`. The
 * server merges a lone key against the stored row before the ordering CHECK
 * runs, so sending the unchanged half would re-assert a value nobody touched.
 * One key per save, exactly as everywhere else on this page.
 *
 * ⚠️ **The refusal can therefore name the OTHER date.** `startDate` alone can
 * come back naming `endDate`, which has its own control that is not the one
 * open — so that message is unroutable against this save's single key and
 * renders as prose on the field being edited, which is where the reader is
 * looking.
 *
 * Plain `YYYY-MM-DD` end to end: `type="date"`'s value IS that text, so nothing
 * here ever builds a `Date` and renders the day before west of Greenwich.
 */
export function StartDateField({ row, writer }: TrackerFieldProps) {
  return (
    <InlineField
      label="Start date"
      type="date"
      value={row.startDate ?? ''}
      display={
        row.startDate ? (
          <span className="whitespace-nowrap text-xs text-fg-secondary tabular">
            {formatDateOnly(row.startDate)}
          </span>
        ) : undefined
      }
      validate={validateDate}
      onSave={(next) => writer.save('startDate', { startDate: dateOrNull(next) })}
      {...gate(writer)}
    />
  );
}

export function EndDateField({ row, writer }: TrackerFieldProps) {
  return (
    <InlineField
      label="End date"
      type="date"
      value={row.endDate ?? ''}
      display={
        row.endDate ? (
          <span className="whitespace-nowrap text-xs text-fg-secondary tabular">
            {formatDateOnly(row.endDate)}
          </span>
        ) : undefined
      }
      validate={validateDate}
      onSave={(next) => writer.save('endDate', { endDate: dateOrNull(next) })}
      {...gate(writer)}
    />
  );
}

/**
 * The typed note that sits under the DERIVED billing status.
 *
 * The badge above it is `billing.status`, read from server-side flags, and it
 * stays read-only: nothing here writes a billing state. This is the one part of
 * that cell a person owns.
 */
export function BillingNoteField({ row, writer }: TrackerFieldProps) {
  return (
    <InlineField
      label="Billing note"
      kind="multiline"
      value={row.billingNote ?? ''}
      maxLength={BILLING_NOTE_MAX}
      rows={3}
      display={
        row.billingNote && row.billingNote.trim() !== '' ? (
          <span className="text-2xs text-fg-muted [overflow-wrap:anywhere]">{row.billingNote}</span>
        ) : undefined
      }
      onSave={(next) => writer.save('billingNote', { billingNote: textOrNull(next) })}
      {...gate(writer)}
    />
  );
}

/**
 * The internal NOTE of the line this row comes from (owner, 2026-10-18) — the
 * same note the deal, quote and invoice line editors show under the product.
 *
 * ⚠️ **NOT A TYPED OVERLAY FIELD, so it does not go through `writer.save`.**
 * `PUT /client-deliverables/line-note` writes THROUGH to the source line by the
 * row's `key`: a quote or invoice line, the parent line of an inclusion or a
 * piece, the deal line of a deal placeholder, and only a true standalone row
 * keeps its own copy. That is why this field is open on INCLUSION rows, where
 * every overlay editor is refused: the write names the line, not an overlay.
 *
 * ⚠️ **Every row sharing the source line changes with it** (its pieces, its
 * inclusions), so `onWritten` must re-read the list; replacing only this row
 * would leave its siblings stating the old note. The row itself is patched in
 * place first, through `onSaved`, so the cell does not wait on the re-read.
 *
 * Gated on the client-deliverables update grant, like every tracker edit, and
 * deliberately not on the deals, quotes or invoices grants (owner ruling).
 * `undefined` is an older API that does not report the note: nothing renders.
 */
export function LineNoteTrackerField({
  row,
  onSaved,
  onWritten,
}: {
  row: ClientDeliverable;
  onSaved: (next: ClientDeliverable) => void;
  onWritten: () => void;
}) {
  const { ability } = useAuth();
  const canUpdate = ability.canUpdate('deliverables');
  const permissionReason = denialReason(ability, 'update', 'deliverables');
  if (row.lineNote === undefined) return null;
  const current = row.lineNote;

  const save = async (next: string): Promise<InlineSaveResult> => {
    const note = textOrNull(next);
    try {
      const response = await api.setDeliverableLineNote(row.key, note);
      onSaved({ ...row, lineNote: response.data.lineNote });
      onWritten();
      return { status: 'saved' };
    } catch (caught) {
      if (caught instanceof ApiError) {
        if (caught.status === 403) {
          return {
            status: 'error',
            message: `${
              denialReason(ability, 'update', 'deliverables') ??
              'Your account no longer has permission to change this row.'
            } Nothing was saved.`,
          };
        }
        if (caught.status === 404) {
          return {
            status: 'error',
            message: 'This row is no longer on the tracker, so the change could not be saved.',
          };
        }
        const { fields, unroutable } = caught.routeFieldErrors(['note']);
        const fieldText = Object.values(fields).join(' ');
        if (fieldText !== '') return { status: 'error', message: fieldText };
        if (unroutable.length > 0) {
          return { status: 'warning', message: `${unroutable.join(' ')} Nothing was saved.` };
        }
        return { status: 'error', message: caught.message };
      }
      return {
        status: 'error',
        message: 'Could not reach the server. Check your connection and try again.',
      };
    }
  };

  const disabledReason = canUpdate
    ? undefined
    : (permissionReason ?? 'Your account does not have permission to change this.');

  return (
    <InlineField
      label="Notes"
      kind="multiline"
      value={current ?? ''}
      maxLength={LINE_NOTE_MAX}
      rows={3}
      display={
        current && current.trim() !== '' ? (
          <span className="whitespace-pre-wrap text-2xs text-fg-muted [overflow-wrap:anywhere]">
            {current}
          </span>
        ) : undefined
      }
      onSave={save}
      disabled={!canUpdate}
      {...(disabledReason === undefined ? {} : { disabledReason })}
    />
  );
}

/**
 * The workbook's "Ads plan" — typed free text, because nothing on a quote or
 * invoice line records one.
 *
 * ⚠️ **EXPECT EVERY ROW TO BE EMPTY AND SAY NOTHING ABOUT IT.** Measured from
 * the workbook's own sheet XML: the column carries zero values across three
 * years. A column of dashes is the DATA, so it gets the same empty mark every
 * other unset value gets and no sentence of its own.
 */
export function AdsPlanField({ row, writer }: TrackerFieldProps) {
  return (
    <InlineField
      label="Ads plan"
      kind="multiline"
      value={row.adsPlan ?? ''}
      maxLength={ADS_PLAN_MAX}
      rows={4}
      display={
        row.adsPlan && row.adsPlan.trim() !== '' ? (
          <span className="text-xs text-fg-secondary [overflow-wrap:anywhere]">{row.adsPlan}</span>
        ) : undefined
      }
      onSave={(next) => writer.save('adsPlan', { adsPlan: textOrNull(next) })}
      {...gate(writer)}
    />
  );
}
